import { GuidedWorkflowTextData } from './guided-workflow-text.model';

export const GuidedWorkflowTextDataES: GuidedWorkflowTextData = {
  title: 'Start work',
  launcher: {
    label: 'Start work',
    tooltip: 'Start work — a guided path from upload to export',
  },
  changeChoice: 'Change choice',
  welcome: {
    title: 'Remediate a PDF, from upload to export',
    body: 'Four stages, done on the real screens: upload a PDF, open it, run the accessibility checker and fix what it reports, then export the tagged PDF. The guide points at each control and waits for you.',
    primary: 'Begin',
  },
  resume: {
    title: 'Continue where you left off?',
    body: 'You already uploaded “{{file}}” in this workflow. Continue with that file, or start over with a new upload.',
    bodyNoName: 'You already uploaded a file in this workflow. Continue with that file, or start over with a new upload.',
    continue: 'Continue',
    startOver: 'Start over',
  },
  steps: {
    add: {
      title: 'Start with an upload',
      body: 'Every job begins with a file. This button on the dashboard opens the upload screen.',
      note: 'Click the + button',
    },
    choice: {
      title: 'Which PDF do you want to remediate?',
      body: 'Upload a document of your own, or practise on one of the three onboarding walkthrough files — a simple, a complex and a scanned PDF.',
      bodyNoSamples: 'Upload a document of your own to continue.',
      own: 'Upload my own file',
      sample: 'Use an onboarding file',
    },
    drop: {
      title: 'Add your file',
      body: 'Drag a PDF onto this area, or click it to pick a file from your computer. The guide moves on as soon as one file is listed.',
      note: 'Click to select a file, or drag and drop one here',
      missing: 'The upload area is not available for this account.',
    },
    uploadBtn: {
      title: '{{count}} file(s) ready to upload',
      body: 'Check the list, then send it. If you remove every file, the guide goes back to the upload area.',
      note: 'Click Upload File',
      missing: 'The Upload button appears once a file is in the list and nothing blocks the upload.',
    },
    sample: {
      title: 'Pick an onboarding file',
      body: 'Each walkthrough file is a different kind of document: simple, complex or scanned. Choose one and it is uploaded for you.',
      note: 'Click one of the three files',
      missing: 'The onboarding walkthrough files are not offered on this plan. Choose “Change choice” to upload your own file.',
    },
    confirm: {
      title: 'Confirm the upload',
      body: 'Confirm to upload the onboarding file and begin.',
      note: 'Click Yes',
    },
    uploading: {
      title: 'Uploading…',
      body: 'Keep this page open. When the upload finishes you are taken to the next screen and the guide continues there.',
    },
    card: {
      yourFile: 'Your file',
      titleProcessing: 'Your file is being prepared',
      bodyProcessing: '“{{file}}” is uploaded. PREP is now reading and auto-tagging it, which can take a few minutes. Stay on this page — this card updates as soon as the file is ready.',
      titleReady: 'Ready to remediate',
      bodyReady: '“{{file}}” is processed and tagged. Open it to check and correct the tags.',
      noteReady: 'Click the file name',
      titleFailed: 'Processing failed',
      bodyFailed: '“{{file}}” could not be processed. Exit the guide, then upload another file or retry from the file’s menu.',
    },
    cardMissing: {
      title: 'Open your file from the list',
      body: 'The uploaded file is not on this page of the list yet. Refresh the list, wait until the file is processed, then click its name. The guide continues in the remediation screen.',
      next: 'Got it',
    },
    orders: {
      title: 'Sent as a service request',
      body: 'This upload was submitted to the remediation service, so there is nothing for you to remediate. The workflow ends here.',
      next: 'Finish',
    },
  },
  remediation: {
    title: 'Start work — remediation',
    skipToExport: 'Skip to export',
    welcome: {
      title: 'This is the remediation screen',
      body: 'The document is open with its tags. From here you will run the accessibility checker, go through each issue it reports and correct it, re-run until the checks pass, and export the tagged PDF. Next starts with the checker.',
      next: 'Next',
    },
    verify: {
      bodyIssues: '{{percent}}% of checks pass — {{count}} issue(s) remain. Next shows them one at a time, with a Re-run to grade your fixes; Back changes the standard; Skip to export leaves them open.',
      bodyClean: '100% — every check passes for this standard. Next offers the checker report and the export.',
    },
    export: {
      title: 'Export the tagged PDF',
      titleIssues: 'Export with {{count}} issue(s) left',
      body: 'The Export menu produces the finished file with the tags and fixes you made.',
      note: 'Click Export',
    },
    exportRow: {
      title: 'Download Tagged PDF',
      body: 'This option saves the PDF with all of its tags. A page-range dialog follows: keep the whole document and confirm.',
      note: 'Click “Export as PDF”',
    },
    exporting: {
      title: 'Exporting…',
      body: 'The tagged PDF is being produced; the download starts when it is ready.',
      titleIdle: 'Finish the export',
      bodyIdle: 'Open Export and choose Export as PDF, pick the pages and confirm. Finish without exporting ends the workflow here.',
      finish: 'Finish without exporting',
    },
    done: {
      title: 'Export completed',
      body: 'The tagged PDF has been generated and is available from the dashboard. You uploaded, checked, corrected and exported a document — the whole job. Start work on the dashboard runs it again any time.',
      dashboard: 'Go to dashboard',
      primary: 'Done',
    },
    allPass: {
      title: 'Every check passes',
      body: 'The document meets the selected standard. Download the checker report as proof if you need it, then move on to the export.',
      report: 'Download checker report',
      export: 'Move to export',
    },
  },
  checkerFixit: {
    title: 'Fix checker issues',
    skipAll: 'Skip all',
    steps: {
      intro: {
        title: 'Let’s get every check to pass',
        body: 'The accessibility checker tests the document against WCAG and PDF/UA rules. This guide runs it, walks you to each failing check, re-runs after your fixes and repeats until every check passes.',
        next: 'Let’s go',
      },
      run: {
        title: 'First, run the checker',
        body: 'Run a document-wise check so every page is graded. Open the Checker menu and choose Document wise — the guide steps aside while the check runs and comes back with the results.',
        note: 'Click Checker ▸ Document wise or Page wise',
      },
      filter: {
        title: 'Pick your compliance standard',
        body: 'All checks, WCAG only or PDF/UA only. The results list, the pass rate and this guide follow your choice. Keep the default if you are not sure, then Next.',
        missing: 'The checker pane is not open — press the Checker Report button in the left pane to show the results.',
      },
      issues: {
        title: 'Issue {{n}} of {{m}} · {{group}}',
        body: '{{issue}}',
        bodyWithHelp: '{{issue}}\n\n{{help}}',
        noteAction: 'Open it, correct it on the page, then › for the next issue or Re-run',
        noteStatic: 'Correct it on the page, then › for the next issue or Re-run',
        goTo: 'Go to the tag',
        openMetadata: 'Open document metadata',
        openAltText: 'Open the alt-text panel',
        next: 'Next issue',
        rerun: 'Re-run',
        missing: 'No failing row to point at for this standard — go back to the result and run the check again.',
        onPage: '{{issue}} (page {{page}})',
        fixFonts: 'Fix fonts',
        fixEncoding: 'Fix character encoding',
        noteFix: 'Apply the fix to the document, then re-run the checker; or › for the next issue',
      },
      verify: {
        titleIssues: 'Almost there — {{count}} to go',
        bodyIssues: '{{percent}}% of checks pass — {{count}} issue(s) remain. Next shows them one at a time, with a Re-run to grade your fixes; Back changes the standard; Skip all goes straight to the export.',
        titleClean: 'Everything passes',
        bodyClean: '100% — every check passes for this view. Next takes you to the export.',
        next: 'Next',
        showIssues: 'Show the issues',
      },
      rerunning: {
        title: 'Re-running the checker',
        body: 'Grading your fixes with the same scope as the last run. Back to the result in a moment.',
      },
    },
  },
};
